import { ChangeDetectorRef, Component, DestroyRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AdminUser } from '@Core/models/codegen/admin.models';
import { Area, TenantUser } from '@Core/models/codegen/configuration.models';
import { TenantContext } from '@Core/contexts/tenant-context';
import { NgbActiveModal, NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '@Services/auth.service';
import { UserService } from '@Services/user.service';
import { SmAssignableBadgeVM } from "@Shared/Components/sm-assignable-badge/sm-assignable-badge.viewmodel";
import _ from 'lodash';
import { combineLatest } from 'rxjs';
import { SelectAreaModalComponent } from '../../../Views/Mktg/select-area/select-area-modal.component';
import { SmSimpleDialog, SimpleDialogOptions } from '../sm-simple-dialog/sm-simple-dialog.component';
import { TenantPermissions } from '@Core/enums/codegen/tenant-permissions.enum';
import { SmNomenclaturePipe } from '@Shared/Pipes/sm-nomenclature.pipe';
import { AsyncPipe } from '@angular/common';

@Component({
    standalone: false,
    selector: 'unity-header',
    templateUrl: './unity-header.component.html',
    styleUrls: ['./unity-header.component.scss']
})
export class UnityHeaderComponent implements OnInit {
    @Input() view: string;

    private destroyRef = inject(DestroyRef);

    public user: TenantUser;
    public adminUser: AdminUser;
    public badge: SmAssignableBadgeVM;
    public areas: Area[] = [];
    public filteredAreas: Area[] = [];
    public areaKey: string;
    public smNomenclaturePipe: SmNomenclaturePipe;
    public asyncPipe: AsyncPipe;

    public TENANT_PERMISSIONS = TenantPermissions;

    @ViewChild('notifyPopover') public notifyPopover: NgbPopover;

    constructor(
        private router: Router,
        private userService: UserService,
        private authService: AuthService,
        private modalService: NgbModal,
        private tenantContext: TenantContext,
        private cdRef: ChangeDetectorRef,
    ) {
        this.asyncPipe = new AsyncPipe(this.cdRef);
    }

    ngOnInit() {
        this.getUserAndBadge();
        this.smNomenclaturePipe = new SmNomenclaturePipe(this.tenantContext);

        if (this.view != 'Admin') {
            combineLatest([
                this.authService.authContext$,
                this.tenantContext.getStore(new Area()).values
            ])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(([authContext, areas]) => {
                    if (!authContext || !areas?.length) {
                        return;
                    }

                    this.getUserAreas(areas);
                });
        }
    }

    private getUserAreas(areas: Area[]): void {
        var areasUserHasAccessTo = this.authService.getAvailableAreasForCurrentTenant();
        // Get the areas and sort based on order
        this.areas = _.sortBy(areas, a => a.Order);
        // Clear filtered Areas
        this.filteredAreas = [];

        _.each(areasUserHasAccessTo, area => {
            var foundArea = _.find(this.areas, a => a.Name == area)
            if (foundArea)
                this.filteredAreas.push(foundArea);
        });

        this.filteredAreas = _.sortBy(this.filteredAreas, a => a.Order);
    }

    private getUserAndBadge(): void {
        if (this.view == 'Admin') {
            this.userService.AdminUser$
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(adminUser => {
                    if (!adminUser) return;

                    this.adminUser = adminUser;
                    this.buildUserBadge();
                });
        } else {
            this.userService.User$
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(tenantUser => {
                    if (!tenantUser) return;

                    this.user = tenantUser;
                    this.buildUserBadge();
                });
        }
    }

    private buildUserBadge() {
        if (!(this.view == 'Admin')) {
            this.badge = {
                Type: "User",
                Id: this.user.Id,
                Name: this.user.DisplayName,
                Initials: this.user.Initials,
                Title: this.user.Title,
                Email: this.user.Email,
                Disabled: !this.user.Active,
                IsCurrentUser: this.user.Id == this.authService.getIdentityKey()
            };
        }
        else {
            this.badge = {
                Type: "User",
                Id: this.adminUser.Id,
                Name: this.adminUser.DisplayName,
                Initials: this.adminUser.DisplayName.charAt(0).toUpperCase(),
                Title: "",
                Email: this.adminUser.Logon,
                Disabled: !this.adminUser.IsActive,
                IsCurrentUser: this.adminUser.Id == this.authService.getIdentityKey()
            }
        }
    }

    public get currentTenant(): string {
        if (this.authService.spaName == 'admin') {
            return "Admin";
        } else {
            return this.authService.getTenantDisplayName();
        }
    }

    public get availableTenants(): string[] {
        if (this.authService.spaName == 'work'
            || this.authService.spaName == 'mgmt'
            || this.authService.spaName == 'conf') {
            return this.authService.getAvailableTenants();
        } else {
            return [];
        }
    }

    public onTenantClick(): void {

        const dialogOptions: SimpleDialogOptions = {

            message: "Are you sure you want to switch tenants?",
            title: 'Switch Tenants',
            buttons: {
                leftButton: {
                    label: 'Confirm',
                    action: function (activeModal: NgbActiveModal) {
                        activeModal.close('cancel');
                    }
                },
                rightButton: {
                    label: 'Cancel',
                    action: function (activeModal: NgbActiveModal) {
                        activeModal.dismiss('cancel button');
                    }
                }
            }
        };

        const modalRef = this.modalService.open(SmSimpleDialog, { backdrop: 'static', centered: true });
        modalRef.componentInstance.options = dialogOptions;

        modalRef.result.then(() => {
            this.router.navigateByUrl("");
        }, () => { });
    }

    public get currentAreaOrConfig(): string {
        if (this.authService.spaName == 'work' || this.authService.spaName == 'mgmt') {
            var areaKey = this.authService.getAreaKey();
            var area = this.filteredAreas.find(a => a.Name == areaKey);
            if (area) {
                var areaAliasOrName = area.Alias ?? area.Name;
                return areaAliasOrName;
            }
        } else if (this.authService.spaName == 'conf') {
            return 'Config';
        }
        return null;
    }

    public get availableAreas(): Area[] {
        if (this.authService.spaName == 'work' || this.authService.spaName == 'mgmt') {
            return this.filteredAreas;
        } else if (this.authService.spaName == 'conf') {
            return [];
        } else if (this.authService.spaName == 'admin') {
            return [];
        }
        return [];
    }

    public get hideMenu(): boolean {
        if (this.authService.spaName == 'work' || this.authService.spaName == 'mgmt')
            return !this.authService.getAreaKey();
        return (this.view == 'Admin');
    }

    public onAreaClick(): void {
        const modalRef = this.modalService.open(SelectAreaModalComponent, {
            ariaLabelledBy: 'modal-basic-title',
            backdrop: "static",
            windowClass: "no-bg"
        });
        const modalComponent = modalRef.componentInstance as SelectAreaModalComponent;

        var area = this.currentAreaOrConfig;
        modalComponent.selectedAreaAliasOrName = area;
        modalComponent.areas = this.filteredAreas;

        // The modal will handle the logic.
        modalRef.result.then(() => { })
            .catch(() => { });
    }

    public onLogoClick(): void {
        this.router.navigate([this.view]);
    }


};
